import api from "../api";

export interface IntegrationsResponse {
  id: number;
  client: string;
  integration: string;
  type: string;
  frequency: string;
  timezone: string;
  next_run: number | null;
  last_run: number | null;
  completed: boolean;
  enabled: boolean;
  error_message: string | null;
}

export interface IntegrationsHistoryItem {
  date: number;
  completed: boolean;
  error_message?: string | null;
}

export interface IntegrationDetails {
  client: string;
  client_name: string;
  timezone: string;
  integration: string;
  type: string;
  frequency: string[];
}

export interface IntegrationsHistoryResponse {
  count: number;
  next: string | null;
  previous: string | null;
  integration_details: IntegrationDetails;
  results: IntegrationsHistoryItem[];
}

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export interface UpdateIntegrationNameParams {
  connection_match_id: number;
  new_integration_name: string;
}

export interface UpdateIntegrationNameResponse {
  success: boolean;
  message: string | null;
  error: string | null;
  status: number | null;
}

/** Connection field as returned by GET settings (secrets masked). */
export interface VendorConnectionRead {
  id: number;
  vendor: string;
  type: string;
  host: string | null;
  port: string | null;
  username: string;
  client_code: string | null;
  password: string;
  password_is_set: boolean;
  passphrase: string;
  passphrase_is_set: boolean;
  key: string;
  key_is_set: boolean;
  access_token: string;
  access_token_is_set: boolean;
}

export interface IntegrationSettingsResponse {
  enabled: boolean;
  source: VendorConnectionRead;
  destination: VendorConnectionRead;
}

/** Partial update: only include fields to change. */
export interface IntegrationSettingsUpdateParams {
  enabled?: boolean;
  source?: Partial<Record<keyof VendorConnectionRead, string | null>>;
  destination?: Partial<Record<keyof VendorConnectionRead, string | null>>;
}

export interface GetAllVendorsParams {
  // Deprecated: frontend-calculated ranges (kept for backward compatibility)
  next_run_from?: string;
  next_run_to?: string;
  last_run_from?: string;
  last_run_to?: string;
  // Preferred: send string filters matching nextRunFilterOptions/lastRunFilterOptions
  next_run?: string; // e.g., "today" | "this_week" | "next_week" | ""
  last_run?: string; // e.g., "yesterday" | "this_week" | "last_week" | ""
  search?: string;
  sort_by?:
    | "client"
    | "integration"
    | "type"
    | "frequency"
    | "last_run"
    | "next_run"
    | "completed"
    | "enabled";
  sort_order?: "asc" | "desc";
  page?: number;
  page_size?: number;
}

export interface GetIntegrationHistoryParams {
  integrationId: number;
  sort_by?: "client" | "date" | "completed" | "timezone" | "error_message";
  sort_order?: "asc" | "desc";
  page?: number;
  page_size?: number;
}

export interface IntegrationUserFolderDto {
  id: number;
  folder: string;
  folder_prefix: string;
  sync_status: string;
  s3_bucket: string;
}

export interface IntegrationUserFolderResponse {
  success: boolean;
  user_folder: IntegrationUserFolderDto | null;
  message?: string | null;
}

export const nextRunFilterOptions = [
  { value: "", label: "All" },
  { value: "today", label: "Today" },
  { value: "this_week", label: "This Week" },
  { value: "next_week", label: "Next Week" },
] as const;

export const lastRunFilterOptions = [
  { value: "", label: "All" },
  { value: "yesterday", label: "Yesterday" },
  { value: "this_week", label: "This Week" },
  { value: "last_week", label: "Last Week" },
] as const;

export const integrationsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getAllVendors: builder.query<
      PaginatedResponse<IntegrationsResponse>,
      GetAllVendorsParams
    >({
      query: (params = {}) => ({
        url: "/web/vendors/",
        method: "GET",
        params,
      }),
    }),

    getIntegrationsHistory: builder.query<
      IntegrationsHistoryResponse,
      GetIntegrationHistoryParams
    >({
      query: ({
        integrationId,
        sort_by = "date",
        sort_order = "desc",
        page = 1,
        page_size = 15,
      }) => ({
        url: `/web/vendors/${integrationId}/`,
        method: "GET",
        params: {
          sort_by,
          sort_order,
          page,
          page_size,
        },
      }),
    }),

    getIntegrationUserFolder: builder.query<
      IntegrationUserFolderResponse,
      number
    >({
      query: (connectionMatchId) => ({
        url: `/web/vendors/${connectionMatchId}/user-folder/`,
        method: "GET",
      }),
    }),

    updateIntegrationName: builder.mutation<
      UpdateIntegrationNameResponse,
      UpdateIntegrationNameParams
    >({
      query: ({
        connection_match_id,
        new_integration_name,
      }: UpdateIntegrationNameParams) => ({
        url: `/web/vendors/${connection_match_id}/`,
        method: "PUT",
        body: { new_integration_name },
      }),
    }),

    getIntegrationSettings: builder.query<
      IntegrationSettingsResponse,
      number
    >({
      query: (connectionMatchId) => ({
        url: `/web/vendors/${connectionMatchId}/settings/`,
        method: "GET",
      }),
      providesTags: (_result, _err, connectionMatchId) => [
        { type: "IntegrationSettings", id: connectionMatchId },
      ],
    }),

    updateIntegrationSettings: builder.mutation<
      IntegrationSettingsResponse,
      { connectionMatchId: number; body: IntegrationSettingsUpdateParams }
    >({
      query: ({ connectionMatchId, body }) => ({
        url: `/web/vendors/${connectionMatchId}/settings/`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_result, _err, { connectionMatchId }) => [
        { type: "IntegrationSettings", id: connectionMatchId },
      ],
    }),

    getIntegrationSettingsReveal: builder.query<
      { value: string },
      { connectionMatchId: number; connection: "source" | "destination"; field: string }
    >({
      query: ({ connectionMatchId, connection, field }) => ({
        url: `/web/vendors/${connectionMatchId}/settings/reveal/`,
        method: "GET",
        params: { connection, field },
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetAllVendorsQuery,
  useGetIntegrationsHistoryQuery,
  useGetIntegrationUserFolderQuery,
  useUpdateIntegrationNameMutation,
  useGetIntegrationSettingsQuery,
  useUpdateIntegrationSettingsMutation,
  useLazyGetIntegrationSettingsRevealQuery,
} = integrationsApi;
